import { useMemo } from 'react';
import { FadeIn } from '../../../components/animations';
import { Box } from '../../../components/primitives/Box';
import { Grid } from '../../../components/primitives/Grid';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useFunnelTemplates } from '../../../hooks/useLeads';
import { useTranslatedStageName } from '../../../hooks/useTranslatedStageName';
import { colors } from '../../../theme/colors';
import {
  type DateRange,
  getDateRange,
  type TimeRange,
  useDashboardSummary,
} from '../hooks/useAnalyticsData';
import { useMetricDisplay } from '../hooks/useMetricDisplay';
import {
  type StageDropoffItem,
  type TimeInStageItem,
  useCycleTime,
  useStageDropoff,
  useTimeInStage,
} from '../hooks/usePipelineAnalytics';
import { labelValue, percentValue, trendBadge } from '../utils/metricDisplay';
import { TabEmptyState, TabErrorState, TabLoadingState } from './AnalyticsLoadingState';
import { AnalyticsMetricCard } from './AnalyticsMetricCard';
import { ChartCard } from './ChartCard';
import { ConversionFunnel, type FunnelStage } from './ConversionFunnel';
import { TimeInStageChart } from './TimeInStageChart';

// The slowest stage among those leads actually moved through. A stage nobody
// passed through has no measured transit time, so it cannot be the bottleneck
// however high its (unmeasured) average reads.
//
// `avgDays === null` is now the whole test. The old predicate also excluded
// `avgDays === 0`, which conflated a stage nobody has finished with one every
// lead crosses the same day — the second is a real, and very fast, measurement.
function resolveBottleneck(data: TimeInStageItem[] | undefined): string | null {
  const measured = (data ?? []).filter(
    (stage): stage is TimeInStageItem & { avgDays: number } => stage.avgDays !== null,
  );
  if (measured.length === 0) return null;
  return [...measured].sort((a, b) => b.avgDays - a.avgDays)[0].stageName;
}

interface FunnelTabProps {
  timeRange: TimeRange;
}

export function FunnelTab({ timeRange }: Readonly<FunnelTabProps>) {
  const { currentOrganization } = useAuth();
  const { t } = useTranslation();
  const { daysValue } = useMetricDisplay();
  const translateStageName = useTranslatedStageName();
  const orgId = currentOrganization?.id;
  const dateRange: DateRange = useMemo(() => getDateRange(timeRange), [timeRange]);

  const { data: templates } = useFunnelTemplates(orgId);
  const activeTemplate = templates?.find((t) => t.isActive);
  const templateId = activeTemplate?.id;

  const { data: summary, isLoading, isError, refetch } = useDashboardSummary(orgId, timeRange);
  const { data: cycleTime } = useCycleTime(orgId, dateRange);
  const { data: timeInStage } = useTimeInStage(orgId, templateId, dateRange);
  const { data: stageDropoff } = useStageDropoff(orgId, templateId, dateRange);

  if (isLoading) return <TabLoadingState cardCount={3} />;
  if (isError) return <TabErrorState onRetry={refetch} />;
  if (!summary) return <TabEmptyState />;

  const kpis = summary.kpis;

  const rawBottleneckStage = resolveBottleneck(timeInStage);
  // Stage labels come from one source across the whole tab. Rendering some of
  // them raw put the same stage on screen twice under two different names.
  const bottleneckStage = rawBottleneckStage ? translateStageName(rawBottleneckStage) : null;
  // `cycleTime` undefined means the request has not resolved; its `avgDays`
  // being null means it resolved and there is genuinely nothing to average.
  const avgCycleDays = cycleTime ? cycleTime.avgDays : kpis.avgCycleTimeDays;

  const funnelStages: FunnelStage[] = (summary.stageDistribution ?? [])
    .sort((a, b) => a.position - b.position)
    .map((s) => ({
      id: s.stageId,
      name: s.stageName,
      count: s.count,
      // Rendered as given. The API computes conversion over the leads that were
      // ever in each stage; a share of `totalLeads` guarded by `|| 1` answered a
      // different question, and the component ignored it anyway.
      percentage: s.conversionPct ?? null,
      color: s.color,
    }));

  return (
    <VStack spacing="xl">
      <FadeIn delay={100}>
        <Grid columns={{ mobile: 1, tablet: 3, desktop: 3 }} spacing="xl">
          <AnalyticsMetricCard
            title={t('analytics.funnel.metrics.avgCycleTime')}
            value={daysValue(avgCycleDays)}
            subtitle={
              cycleTime
                ? t('analytics.funnel.metrics.median', {
                    value: daysValue(cycleTime.medianDays),
                  })
                : undefined
            }
            metricType="campaigns"
            index={0}
          />
          <AnalyticsMetricCard
            title={t('analytics.overview.metrics.winRate')}
            value={percentValue(kpis.conversionRate)}
            subtitle={t('analytics.overview.metrics.winRateSubtitle')}
            trend={trendBadge(kpis.conversionGrowth)}
            metricType="conversion"
            index={1}
          />
          <AnalyticsMetricCard
            title={t('analytics.funnel.metrics.bottleneck')}
            value={labelValue(bottleneckStage)}
            subtitle={t('analytics.funnel.metrics.bottleneckSubtitle')}
            metricType="visitors"
            index={2}
          />
        </Grid>
      </FadeIn>

      {funnelStages.length > 0 && (
        <FadeIn delay={300}>
          <ChartCard
            title={t('analytics.overview.conversionFunnel')}
            subtitle={t('analytics.funnel.conversionFunnelSubtitle')}
            accentColor="cyan"
            delay={350}
          >
            <Box style={{ minHeight: 200 }}>
              <ConversionFunnel stages={funnelStages} showDropIndicators />
            </Box>
          </ChartCard>
        </FadeIn>
      )}

      {timeInStage && timeInStage.length > 0 && (
        <FadeIn delay={450}>
          <ChartCard
            title={t('analytics.funnel.timeInStage.title')}
            subtitle={t('analytics.funnel.timeInStage.subtitle')}
            accentColor="purple"
            delay={500}
          >
            <TimeInStageChart data={timeInStage} />
          </ChartCard>
        </FadeIn>
      )}

      {stageDropoff && stageDropoff.length > 0 && (
        <FadeIn delay={600}>
          <ChartCard
            title={t('analytics.funnel.stageDropoff.title')}
            subtitle={t('analytics.funnel.stageDropoff.subtitle')}
            accentColor="cyan"
            delay={650}
          >
            <StageDropoffSection data={stageDropoff} />
          </ChartCard>
        </FadeIn>
      )}
    </VStack>
  );
}

// `key` names both the percentage field on the API item and the label under
// `analytics.funnel.stageDropoff.categories`, so the two cannot drift apart.
const DROPOFF_CATEGORIES = [
  { key: 'advanced', color: colors.status.success.main },
  // Brightest green of the set: won is the best outcome a stage can report, and
  // it must not be mistaken at a glance for `advanced`, which is still in play.
  { key: 'won', color: colors.status.success.light },
  { key: 'active', color: colors.status.info.main },
  { key: 'stale', color: colors.status.warning.main },
  { key: 'lost', color: colors.status.error.main },
  { key: 'regressed', color: colors.text.tertiary },
] as const;

// The six categories partition the stage population, so they sum to 100%.
// A stage nobody was in during the window has no distribution at all: every
// share is null and renders as an em dash, never as 0% or 100%.
function StageDropoffSection({ data }: Readonly<{ data: StageDropoffItem[] }>) {
  const { t } = useTranslation();
  const translateStageName = useTranslatedStageName();
  const sorted = [...data].sort((a, b) => a.position - b.position);

  return (
    <VStack spacing="lg">
      {sorted.map((stage, index) => (
        <FadeIn key={stage.stageId} delay={index * 60}>
          <Box padding="lg">
            <VStack spacing="sm">
              <HStack justify="between" align="center">
                <Text variant="body" color={colors.text.primary} style={{ fontWeight: '600' }}>
                  {translateStageName(stage.stageName)}
                </Text>
                <Text variant="caption" color={colors.text.tertiary}>
                  {t('analytics.funnel.stageDropoff.population', { count: stage.population })}
                </Text>
              </HStack>
              <HStack spacing="lg" wrap>
                {DROPOFF_CATEGORIES.map((category) => (
                  <HStack key={category.key} spacing="xs" align="center">
                    <Box
                      backgroundColor={category.color}
                      borderRadius="sm"
                      style={{ width: 8, height: 8 }}
                    />
                    <Text variant="caption" color={category.color}>
                      {`${t(`analytics.funnel.stageDropoff.categories.${category.key}`)}: ${percentValue(stage[`${category.key}Pct`])}`}
                    </Text>
                  </HStack>
                ))}
              </HStack>
            </VStack>
          </Box>
        </FadeIn>
      ))}
    </VStack>
  );
}
